import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiDollarSign } from 'react-icons/fi';
import { FaCcVisa } from 'react-icons/fa';
import api from '../services/api';

interface Payment {
  date: string;
  month: string;
  amount: number;
  type: string | null;
  status: 'Pending' | 'Completed' | 'Failed' | 'Processing' | null;
}

interface PaymentHistoryResponse {
  message: string;
  data: Payment[];
}

export default function PaymentHistory() {
  const navigate = useNavigate();
  const [payments, setPayments] = useState<Payment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPayments = async () => {
      try {
        const response = await api.get<PaymentHistoryResponse>('/payments');
        setPayments(response.data.data);
      } catch (err) {
        setError('Failed to load payment history');
        console.error('Failed to fetch payments:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPayments();
  }, []);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getPaymentIcon = (type: string | null) => {
    if (!type) return null;
    
    switch (type.toLowerCase()) {
      case 'mastercard':
        return <img src="/mc_icon.svg" alt="Mastercard" className="h-6 w-6" />;
      case 'visa':
        return <FaCcVisa className="h-6 w-6 text-[#1A1F71]" />;
      default:
        return <FiDollarSign className="h-5 w-5 text-primary dark:text-primary-400" />;
    }
  };

  const getStatusBadgeClasses = (status: string | null) => {
    if (!status) return '';

    switch (status.toLowerCase()) {
      case 'completed':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
      case 'failed':
        return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
      case 'processing':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400';
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-48 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <div className="h-6 w-32 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
          </div>
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {[1, 2, 3].map((i) => (
              <div key={i} className="p-4">
                <div className="animate-pulse flex justify-between items-center">
                  <div className="flex items-center space-x-4">
                    <div className="h-10 w-10 bg-gray-200 dark:bg-gray-700 rounded-full"></div>
                    <div className="space-y-2">
                      <div className="h-4 w-32 bg-gray-200 dark:bg-gray-700 rounded"></div>
                      <div className="h-3 w-24 bg-gray-200 dark:bg-gray-700 rounded"></div>
                    </div>
                  </div>
                  <div className="h-4 w-20 bg-gray-200 dark:bg-gray-700 rounded"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
        <div className="text-red-600 dark:text-red-400">{error}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate('/fees')}
          className="inline-flex items-center text-sm text-primary hover:text-primary-dark dark:text-primary-400 dark:hover:text-primary-300"
        >
          <FiArrowLeft className="h-4 w-4 mr-1" />
          Back to Fees
        </button>
      </div>

      <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Payment History
          </h2>
        </div>

        <div className="divide-y divide-gray-200 dark:divide-gray-700">
          {payments.map((payment, index) => (
            <div 
              key={index}
              className="p-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700/50"
            >
              <div className="flex items-center space-x-4">
                <div className="h-10 w-10 flex items-center justify-center bg-gray-100 dark:bg-gray-700 rounded-full">
                  {getPaymentIcon(payment.type)}
                </div>
                <div>
                  <div className="font-medium text-gray-900 dark:text-white">
                    {payment.month}
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    {formatDate(payment.date)}
                    {payment.status && (
                      <span className={`ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getStatusBadgeClasses(payment.status)}`}>
                        {payment.status}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="font-medium text-gray-900 dark:text-white">
                {formatCurrency(payment.amount)}
              </div>
            </div>
          ))}

          {payments.length === 0 && (
            <div className="p-8 text-center text-gray-500 dark:text-gray-400">
              No payment history available
            </div>
          )}
        </div>
      </div>
    </div>
  );
}